import MuiTableRow from '@material-ui/core/TableRow';
import React from 'react';
import styled from 'styled-components';
import { Checkbox } from '../Checkbox';
import { StyledTableCell, TableCell } from './TableCell';
import { BaseTableRow, RowStatus, TableColumn } from './types';

export const StyledTableRow = styled(MuiTableRow)<{
  $hideRow?: boolean;
  $isSelected?: boolean;
  $rowStatus?: RowStatus;
  $isDropTarget?: boolean;
}>`
  ${({ $hideRow }) => ($hideRow ? 'visibility: collapse;' : '')}
  ${({ theme, $isSelected }) =>
    $isSelected
      ? `
    background-color: ${theme.colors.grayf4};
    & > .MuiTableCell-root {
      background-color: ${theme.colors.grayf4};
    }
  `
      : ''}
  ${({ theme, $rowStatus }) => {
    switch ($rowStatus) {
      case RowStatus.ERROR:
        return `background-color: ${theme.colors.red08};`;
      case RowStatus.WARNING:
        return `background-color: ${theme.colors.warningFill};`;
      case RowStatus.SUCCESS:
        return `background-color: ${theme.colors.green08};`;
      default:
        return '';
    }
  }}
  ${({ theme, $isDropTarget }) =>
    $isDropTarget
      ? `
    background-color: ${theme.colors.blue08};
    outline: 2px dashed ${theme.colors.blue};
    outline-offset: -2px;
  `
      : ''}

  & img {
    max-height: 1.875rem;
    vertical-align: middle;
  }
`;

export type TableRowHtmlProps = React.HTMLAttributes<HTMLTableRowElement> & {
  $isDropTarget?: boolean;
  $isSelected?: boolean;
  'data-dragging'?: string;
};

// Keep column not too wide, without unnecessary padding between it and the column to the left
export const CollapsibleSectionTableCell = styled(StyledTableCell)`
  width: 1rem;
  padding: 0;

  &:first-of-type {
    padding-left: 0;
  }

  & + .MuiTableCell-root {
    padding-left: 0.25rem;
  }
`;

const CheckboxTableCell = styled(StyledTableCell)`
  width: 2.625rem;
`;

interface CommonTableRowProps<RowEntity extends BaseTableRow> {
  columns: TableColumn<RowEntity>[];
  getRowProps?: (row: RowEntity) => TableRowHtmlProps;
  isCollapsibleRow?: boolean;
  isCompact?: boolean;
  row: RowEntity;
  shouldHide?: boolean;
}

type SelectProps<RowEntity extends BaseTableRow> = {
  getCheckboxAriaLabel: (row: RowEntity) => string;
  getIsRowSelected: (row: RowEntity) => boolean;
  isRowSelectionDisabled?: boolean;
  onToggleRow: (row: RowEntity, isChecked: boolean) => void;
};

export type TableRowProps<RowEntity extends BaseTableRow> =
  CommonTableRowProps<RowEntity> & {
    canRowsBeSelected?: boolean;
  } & Partial<SelectProps<RowEntity>>;

export function TableRow<RowEntity extends BaseTableRow>({
  canRowsBeSelected,
  columns,
  getCheckboxAriaLabel,
  getIsRowSelected,
  getRowProps,
  isCompact,
  isCollapsibleRow,
  isRowSelectionDisabled,
  onToggleRow,
  row,
  shouldHide,
}: TableRowProps<RowEntity>) {
  const rowProps = getRowProps?.(row);
  const isSelected =
    (getIsRowSelected?.(row) ?? false) || !!rowProps?.$isSelected;

  return (
    <StyledTableRow
      {...rowProps}
      $hideRow={shouldHide}
      $isSelected={isSelected}
      $rowStatus={row.rowStatus}
      aria-hidden={shouldHide}
    >
      {isCollapsibleRow && <CollapsibleSectionTableCell />}
      {canRowsBeSelected &&
        getCheckboxAriaLabel &&
        getIsRowSelected &&
        onToggleRow && (
          <CheckboxTableCell padding="none" $isCompact={isCompact}>
            <Checkbox
              checked={getIsRowSelected(row)}
              disabled={isRowSelectionDisabled}
              isLabelHidden
              label={getCheckboxAriaLabel(row)}
              onChange={(evt) => onToggleRow(row, evt.target.checked)}
            />
          </CheckboxTableCell>
        )}
      {columns.map((col: TableColumn<RowEntity>) => (
        <TableCell<RowEntity>
          isCompact={isCompact}
          key={col.id}
          col={col}
          row={row}
        />
      ))}
    </StyledTableRow>
  );
}
